// Shown before anything else when an account needs finishing: a one-time password to replace, or
// two-step verification that's required for this person's role.
import { useState } from 'react'
import { landingPath, type Me } from '@briary/shared'
import { useAuth } from '../../lib/auth'
import { Redirect, useRouter } from '../../lib/router'
import { Notice } from '../../ui'
import { ChangePassword, TwoStep } from '../account/sections'
import { AuthLayout } from './AuthLayout'

type Stage = 'password' | 'mfa' | null
const stageFor = (me: Me): Stage => (me.mustChangePassword ? 'password' : me.mfa.required && !me.mfa.totp ? 'mfa' : null)

export default function Setup() {
  const { me, refresh, signOut } = useAuth()
  const { navigate } = useRouter()
  // Fixed when the page opens, so refreshing "me" mid-way (e.g. right after 2FA turns on) doesn't skip
  // past the recovery codes.
  const [stage, setStage] = useState<Stage>(() => (me ? stageFor(me) : null))
  if (!me) return null
  if (!stage) return <Redirect to={landingPath(me)} />

  const next = async () => {
    const m = await refresh()
    const s = m ? stageFor(m) : null
    if (s && s !== stage) setStage(s)
    else if (m) navigate(landingPath(m), { replace: true })
  }

  return (
    <AuthLayout title="Finish setting up">
      {stage === 'password' ? (
        <>
          <h2>Choose your own password</h2>
          <p className="lead">You signed in with a one-time password. Please replace it with one only you know.</p>
          <ChangePassword onDone={next} />
        </>
      ) : (
        <>
          <h2>Turn on two-step verification</h2>
          <Notice tone="info">Your account needs two-step verification before you can continue.</Notice>
          <TwoStep onDone={next} />
        </>
      )}
      <div className="auth-links">
        <a href="#" onClick={(e) => { e.preventDefault(); void signOut() }}>
          Sign out
        </a>
      </div>
    </AuthLayout>
  )
}
